import { ShortCode } from '@highereducation/wordpress';
import { SectionHeader } from '@highereducation/hen-ui';

type HeadingSize = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const VALID_SIZES: HeadingSize[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const VALID_BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'outlined',
  'primaryFill',
  'primaryOutline',
  'textOnly',
  'blackFill',
  'blackOutline',
  'grayOutline',
  'textOnlySecondary',
  'textOnlyWhite',
] as const;

type ButtonVariant = (typeof VALID_BUTTON_VARIANTS)[number];

type SectionHeaderShortcodeProps = {
  size?: string;
  title?: string;
  brow?: string;
  subtext?: string;
  'action-button-text'?: string;
  'action-button-href'?: string;
  'button-text'?: string;
  'button-variant'?: string;
  color?: 'default' | 'white';
  align?: 'left' | 'center';
  'align-md'?: 'left' | 'center';
  [key: string]: unknown;
};

/**
 * SectionHeaderShortcode - Shortcode wrapper for SectionHeader composite component.
 *
 * @example
 * [hen-ui-section-header size="h2" title="Our Mission" brow="About" subtext="Description here." action-button-text="View all" action-button-href="/all" /]
 */
export const SectionHeaderShortcode: ShortCode<SectionHeaderShortcodeProps> = {
  component: ({
    size = 'h2',
    title = '',
    brow,
    subtext,
    'action-button-text': actionButtonText,
    'action-button-href': actionButtonHref,
    'button-text': buttonText,
    'button-variant': buttonVariant,
    color = 'default',
    align = 'left',
    'align-md': alignMd,
  }) => {
    const validSize: HeadingSize = VALID_SIZES.includes(size as HeadingSize)
      ? (size as HeadingSize)
      : 'h2';
    const actionButton =
      actionButtonText && actionButtonHref
        ? { text: actionButtonText, href: actionButtonHref }
        : undefined;
    const validVariant = VALID_BUTTON_VARIANTS.includes(
      buttonVariant as ButtonVariant,
    )
      ? (buttonVariant as ButtonVariant)
      : undefined;
    const button = buttonText
      ? {
          text: buttonText,
          ...(validVariant && { variant: validVariant }),
        }
      : undefined;
    return (
      <SectionHeader
        size={validSize}
        title={title}
        brow={brow}
        subtext={subtext}
        actionButton={actionButton}
        button={button}
        color={color}
        align={align}
        align-md={alignMd}
      />
    );
  },
};
